import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2, XCircle, Eye, Trash2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

export default function AiBlogsAdminTab() {
  const [items, setItems] = useState([]);
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [customTopic, setCustomTopic] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blogs`, { headers: authHeaders() });
      setItems(data.items || []);
      setPool(data.topic_pool || []);
    } catch (e) {
      toast.error("Failed to load blogs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async (topic) => {
    setGenerating(true);
    toast.info("Claude is writing your blog… this takes 15-30 seconds");
    try {
      const { data } = await axios.post(
        `${API}/admin/blogs/generate`,
        { topic: topic || null },
        { headers: authHeaders() }
      );
      toast.success(`Draft ready: ${data.title}`);
      setCustomTopic("");
      await load();
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const publish = async (id) => {
    try {
      await axios.post(`${API}/admin/blogs/${id}/publish`, {}, { headers: authHeaders() });
      toast.success("Published — now live on /blog");
      await load();
      if (preview?.id === id) setPreview({ ...preview, status: "published" });
    } catch (e) {
      toast.error("Publish failed");
    }
  };

  const unpublish = async (id) => {
    try {
      await axios.post(`${API}/admin/blogs/${id}/unpublish`, {}, { headers: authHeaders() });
      toast.success("Moved back to drafts");
      await load();
    } catch (e) {
      toast.error("Unpublish failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this blog permanently?")) return;
    try {
      await axios.delete(`${API}/admin/blogs/${id}`, { headers: authHeaders() });
      toast.success("Deleted");
      if (preview?.id === id) setPreview(null);
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const drafts = items.filter((b) => b.status === "draft");
  const published = items.filter((b) => b.status === "published");

  return (
    <div className="space-y-8" data-testid="ai-blogs-tab">
      {/* Generate section */}
      <div className="tlr-card p-6 md:p-8 space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="label-eyebrow text-[#C9A24A] mb-1">Claude Sonnet 4.6 · Auto-scheduled every 3 days</p>
            <h2 className="font-display text-2xl md:text-3xl text-[#7A1E2A]">AI Blog Studio</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-xl">
              Generate SEO-ready 900-1200 word guides in seconds. Every blog starts as a draft — you review, then publish with one click.
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-display text-[#7A1E2A]">{items.length}</div>
            <div className="text-xs text-gray-500 uppercase tracking-widest">Total blogs</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2 block">Custom topic (optional)</label>
            <div className="flex gap-2">
              <Input
                data-testid="ai-blog-custom-topic"
                value={customTopic}
                onChange={(e) => setCustomTopic(e.target.value)}
                placeholder="e.g. Rent trends in Whitefield Bengaluru 2026"
                disabled={generating}
              />
              <Button
                data-testid="ai-blog-generate-custom"
                onClick={() => generate(customTopic.trim())}
                disabled={generating || !customTopic.trim()}
                className="tlr-gold-btn shrink-0"
              >
                {generating ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                <span className="ml-2">Write</span>
              </Button>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2 block">Or let AI pick a topic</label>
            <Button
              data-testid="ai-blog-generate-random"
              onClick={() => generate(null)}
              disabled={generating}
              variant="outline"
              className="w-full border-[#7A1E2A]/30 text-[#7A1E2A] hover:bg-[#7A1E2A]/8"
            >
              {generating ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
              <span className="ml-2">Auto-pick from {pool.length}+ topics</span>
            </Button>
          </div>
        </div>

        {pool.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-[#7A1E2A] font-medium">See topic pool</summary>
            <div className="mt-3 flex flex-wrap gap-2">
              {pool.map((t) => (
                <button
                  key={t}
                  onClick={() => generate(t)}
                  disabled={generating}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D0] hover:border-[#7A1E2A]/40 hover:text-[#7A1E2A] transition-colors"
                >
                  {t}
                </button>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Drafts queue */}
      <BlogSection
        title={`Drafts (${drafts.length})`}
        items={drafts}
        empty="No drafts. Generate one above."
        onPreview={setPreview}
        onPublish={publish}
        onDelete={remove}
        actionLabel="Publish"
        actionIcon={<CheckCircle2 size={14} />}
      />

      {/* Published */}
      <BlogSection
        title={`Published (${published.length})`}
        items={published}
        empty="Nothing published yet."
        onPreview={setPreview}
        onPublish={unpublish}
        onDelete={remove}
        actionLabel="Unpublish"
        actionIcon={<RotateCcw size={14} />}
      />

      {/* Preview modal */}
      {preview && (
        <PreviewModal
          post={preview}
          onClose={() => setPreview(null)}
          onPublish={() => publish(preview.id)}
          onDelete={() => remove(preview.id)}
        />
      )}
    </div>
  );
}

function BlogSection({ title, items, empty, onPreview, onPublish, onDelete, actionLabel, actionIcon }) {
  return (
    <div className="tlr-card p-6">
      <h3 className="font-display text-xl text-[#7A1E2A] mb-4">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">{empty}</p>
      ) : (
        <ul className="divide-y divide-[#EFE3D0]" data-testid={`blog-list-${actionLabel.toLowerCase()}`}>
          {items.map((b) => (
            <li key={b.id} className="py-4 flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#C9A24A]">{b.category}</span>
                  {b.auto_generated && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#7A1E2A]/8 text-[#7A1E2A]">Auto</span>}
                  <span className="text-xs text-gray-500">{b.read}</span>
                </div>
                <div className="font-semibold text-[#1B0F14] mt-1 truncate">{b.title}</div>
                <div className="text-xs text-gray-500 mt-1 truncate">/blog/{b.slug}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => onPreview(b)} data-testid={`blog-preview-${b.id}`}>
                  <Eye size={14} />
                </Button>
                <Button size="sm" onClick={() => onPublish(b.id)} className="tlr-gold-btn h-8" data-testid={`blog-action-${b.id}`}>
                  {actionIcon} <span className="ml-1">{actionLabel}</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onDelete(b.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50" data-testid={`blog-delete-${b.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PreviewModal({ post, onClose, onPublish, onDelete }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="blog-preview-modal"
      >
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-[#EFE3D0] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-[#C9A24A]">{post.category}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${post.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
              {post.status}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100"><XCircle size={20} /></button>
        </div>
        <div className="px-6 py-6 space-y-4">
          <h1 className="font-display text-3xl text-[#7A1E2A]">{post.title}</h1>
          <p className="text-gray-600 italic">{post.excerpt}</p>
          {post.image && <img src={post.image} alt="" className="rounded-xl w-full h-56 object-cover" />}
          <article className="space-y-6">
            {(post.sections || []).map((s, i) => (
              <section key={i}>
                <h2 className="font-display text-xl text-[#7A1E2A] mb-2">{s.h}</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{s.p}</p>
              </section>
            ))}
          </article>
          <div className="text-xs text-gray-500 pt-4 border-t border-[#EFE3D0]">
            <div>Slug: /blog/{post.slug}</div>
            <div>Keywords: {post.keywords}</div>
            {post.topic_seed && <div>Topic seed: {post.topic_seed}</div>}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-[#EFE3D0] px-6 py-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onDelete} className="text-red-600 hover:bg-red-50">
            <Trash2 size={14} className="mr-1" /> Delete
          </Button>
          <Button onClick={onPublish} className="tlr-gold-btn">
            {post.status === "published" ? <><RotateCcw size={14} className="mr-1" /> Unpublish</> : <><CheckCircle2 size={14} className="mr-1" /> Publish live</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
